import type { TraceAction } from '@chatsdet/trace-tools';
import type { CachedAction, PlaybookStep } from '@chatsdet/types';
import type { LinkerInput, StrictParsedRunCode } from './step-linker.js';
import type { CandidateAction } from './step-linker-groups.js';

export type TraceRole = 'terminal' | 'assistive' | 'observation' | 'unsupported';

export interface LiteralSignal {
  kind: 'url' | 'value' | 'key' | 'option' | 'path';
  literal: string;
}

const TERMINAL_METHODS = new Set([
  'click',
  'dblclick',
  'fill',
  'type',
  'press',
  'check',
  'uncheck',
  'selectOption',
  'goto',
  'goBack',
  'goForward',
  'reload',
  'setInputFiles',
  'dragTo',
]);

const ASSISTIVE_METHODS = new Set(['scrollIntoViewIfNeeded', 'hover', 'mouse']);

export function traceActionMethod(action: TraceAction): string {
  // Deliberately collapse low-level mouse calls to one assistive bucket. That
  // can over-gap mouse-specific traces, but it avoids inferring element targets
  // from pointer plumbing that is not a replayable semantic action.
  if (/^mouse\./i.test(action.title)) return 'mouse';
  const method = action.title.split('.').pop() ?? action.title;
  return method === 'mouseClick' || method === 'mouseMove' ? 'mouse' : method;
}

export function traceActionRole(method: string): TraceRole {
  if (TERMINAL_METHODS.has(method)) return 'terminal';
  if (ASSISTIVE_METHODS.has(method)) return 'assistive';
  if (method === 'screenshot' || method === 'accessibilitySnapshot') return 'observation';
  return 'unsupported';
}

export function isStepTraceCompatible(stepAction: string, candidate: CandidateAction): boolean {
  if (candidate.role === 'assistive') {
    if (stepAction === 'hover') return candidate.method === 'hover';
    if (stepAction === 'scroll') return candidate.method === 'scrollIntoViewIfNeeded';
    return false;
  }
  if (candidate.role !== 'terminal') return false;

  switch (stepAction) {
    case 'click':
    case 'toggle':
      return ['click', 'dblclick', 'check', 'uncheck'].includes(candidate.method);
    case 'fill':
    case 'fill_form':
      return ['fill', 'type'].includes(candidate.method);
    case 'select':
      return candidate.method === 'selectOption';
    case 'navigate':
      return ['goto', 'goBack', 'goForward', 'reload'].includes(candidate.method);
    case 'press_key':
      return candidate.method === 'press';
    case 'hover':
      return candidate.method === 'hover';
    case 'upload':
      return candidate.method === 'setInputFiles';
    case 'drag':
      return candidate.method === 'dragTo';
    default:
      return false;
  }
}

export function stepMatchesCandidate(
  step: PlaybookStep,
  candidate: CandidateAction,
  signal: LiteralSignal | undefined
): boolean {
  if (!isStepTraceCompatible(step.action, candidate)) return false;
  if (!signal || signal.literal.length < 3) return true;
  return traceCandidateLiterals(candidate.action, signal).includes(signal.literal);
}

export function isCachedActionTraceCompatible(
  action: CachedAction,
  candidate: CandidateAction
): boolean {
  switch (action.actionType) {
    case 'click':
      return ['click', 'dblclick', 'check', 'uncheck'].includes(candidate.method);
    case 'type':
    case 'fill_form':
      return ['fill', 'type'].includes(candidate.method);
    case 'select_option':
      return candidate.method === 'selectOption';
    case 'navigate':
      return ['goto', 'goBack', 'goForward', 'reload'].includes(candidate.method);
    case 'press_key':
      return candidate.method === 'press';
    case 'hover':
      return candidate.method === 'hover';
    case 'file_upload':
      return candidate.method === 'setInputFiles';
    case 'drag':
      return candidate.method === 'dragTo';
    case 'scroll':
      return candidate.method === 'scrollIntoViewIfNeeded';
    case 'run_code':
      return true;
    default:
      return false;
  }
}

export function discriminatingStepLiteral(
  step: PlaybookStep,
  provenance: LinkerInput['provenance']
): LiteralSignal | null {
  const params = step.params ?? {};
  const hydrated = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, hydrateProvenanceValue(value, provenance)])
  );

  switch (step.action) {
    case 'navigate': {
      const url = firstString(hydrated.url);
      return url ? { kind: 'url', literal: normalizeUrl(url) } : null;
    }
    case 'fill':
    case 'fill_form': {
      const value = firstString(hydrated.value) ?? firstString(hydrated.text);
      return value ? { kind: 'value', literal: value } : null;
    }
    case 'press_key': {
      const key = firstString(hydrated.key);
      return key ? { kind: 'key', literal: key } : null;
    }
    case 'select': {
      const option =
        firstString(hydrated.option) ?? firstString(hydrated.value) ?? firstString(hydrated.label);
      return option ? { kind: 'option', literal: option } : null;
    }
    case 'upload': {
      const path = firstString(hydrated.path);
      return path ? { kind: 'path', literal: basename(path) } : null;
    }
    default:
      return null;
  }
}

function hydrateProvenanceValue(value: string, provenance: LinkerInput['provenance']): string {
  const replacements: Array<{ expression: string; resolved: string }> = [];
  for (const [key, resolved] of Object.entries(provenance.variableValues)) {
    replacements.push({ expression: `$var.${key}`, resolved });
  }
  for (const entry of provenance.macroResolutionLog ?? []) {
    replacements.push(entry);
  }
  replacements.sort(
    (a, b) => b.expression.length - a.expression.length || a.expression.localeCompare(b.expression)
  );
  if (replacements.length === 0) return value;

  const byExpression = new Map(replacements.map((entry) => [entry.expression, entry.resolved]));
  const pattern = new RegExp(
    replacements.map((entry) => escapeRegex(entry.expression)).join('|'),
    'g'
  );
  return value.replace(pattern, (match) => byExpression.get(match) ?? match);
}

export function cachedActionLiteral(action: CachedAction): LiteralSignal | null {
  switch (action.actionType) {
    case 'navigate': {
      const url = valueFromRecord(action.args, ['url']);
      return url ? { kind: 'url', literal: normalizeUrl(url) } : null;
    }
    case 'type':
    case 'fill_form': {
      const value = valueFromRecord(action.args, ['value', 'text']);
      return value ? { kind: 'value', literal: value } : null;
    }
    case 'press_key': {
      const key = valueFromRecord(action.args, ['key']);
      return key ? { kind: 'key', literal: key } : null;
    }
    case 'select_option': {
      const option = valueFromRecord(action.args, ['option', 'value', 'label']);
      return option ? { kind: 'option', literal: option } : null;
    }
    case 'file_upload': {
      const path = valueFromRecord(action.args, ['path', 'file', 'files']);
      return path ? { kind: 'path', literal: basename(path) } : null;
    }
    default:
      return null;
  }
}

export function traceCandidateLiterals(action: TraceAction, signal: LiteralSignal): string[] {
  const params = action.params ?? {};
  switch (signal.kind) {
    case 'url':
      return valuesFromRecord(params, ['url']).map(normalizeUrl);
    case 'value':
      return valuesFromRecord(params, ['value', 'text']);
    case 'key':
      return valuesFromRecord(params, ['key']);
    case 'option':
      return valuesFromRecord(params, ['option', 'value', 'label', 'values', 'options']);
    case 'path':
      return valuesFromRecord(params, ['path', 'file', 'files', 'localPaths']).map(basename);
  }
}

export function runCodeInteractionMatches(
  interaction: StrictParsedRunCode['interactions'][number],
  candidate: CandidateAction
): boolean {
  if (!runCodeKindCompatible(interaction.kind, candidate.method)) return false;
  if (interaction.value !== undefined) {
    return traceCandidateLiterals(candidate.action, {
      kind: interaction.kind === 'selectOption' ? 'option' : 'value',
      literal: interaction.value,
    }).includes(interaction.value);
  }
  if (interaction.key !== undefined) {
    return traceCandidateLiterals(candidate.action, {
      kind: 'key',
      literal: interaction.key,
    }).includes(interaction.key);
  }
  if (interaction.url !== undefined) {
    const normalized = normalizeUrl(interaction.url);
    return traceCandidateLiterals(candidate.action, { kind: 'url', literal: normalized }).includes(
      normalized
    );
  }
  return true;
}

function runCodeKindCompatible(
  kind: StrictParsedRunCode['interactions'][number]['kind'],
  method: string
): boolean {
  switch (kind) {
    case 'click':
      return method === 'click';
    case 'dblclick':
      return method === 'dblclick';
    case 'fill':
      return method === 'fill';
    case 'type':
      return method === 'type';
    case 'press':
      return method === 'press';
    case 'check':
      return method === 'check';
    case 'uncheck':
      return method === 'uncheck';
    case 'selectOption':
      return method === 'selectOption';
    case 'goto':
      return method === 'goto';
    case 'setInputFiles':
      return method === 'setInputFiles';
    case 'dragTo':
      return method === 'dragTo';
    case 'hover':
      return method === 'hover';
    case 'scroll':
      return method === 'scrollIntoViewIfNeeded';
  }
}

function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  try {
    const url = new URL(trimmed);
    const isDefaultPort =
      (url.protocol === 'http:' && url.port === '80') ||
      (url.protocol === 'https:' && url.port === '443');
    const port = url.port && !isDefaultPort ? `:${url.port}` : '';
    const pathname = url.pathname === '/' ? '' : url.pathname.replace(/\/+$/, '');
    return `${url.protocol.toLowerCase()}//${url.hostname.toLowerCase()}${port}${pathname}${url.search}`;
  } catch {
    const withoutTrailingSlash = trimmed.replace(/\/+$/, '');
    const match = /^([a-z][a-z0-9+.-]*:\/\/)([^/?#]+)(.*)$/i.exec(withoutTrailingSlash);
    if (!match) return withoutTrailingSlash.toLowerCase();
    const [, protocol, host, rest] = match;
    return `${protocol.toLowerCase()}${host.toLowerCase()}${rest}`;
  }
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function basename(value: string): string {
  const normalized = value.replace(/\\/g, '/');
  const parts = normalized.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? value;
}

function valueFromRecord(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = firstString(record[key]);
    if (value) return value;
  }
  return null;
}

function valuesFromRecord(record: Record<string, unknown>, keys: string[]): string[] {
  const out: string[] = [];
  for (const key of keys) {
    out.push(...collectStrings(record[key]));
  }
  return out;
}

function firstString(value: unknown): string | null {
  return collectStrings(value)[0] ?? null;
}

function collectStrings(value: unknown): string[] {
  if (typeof value === 'string') return [value];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) return value.flatMap((entry) => collectStrings(entry));
  if (isRecord(value)) return Object.values(value).flatMap((entry) => collectStrings(entry));
  return [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
